import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Server, ShieldCheck, Copy, Check, Lock, Database, RefreshCw, Network, Layers } from 'lucide-react';
import { apiClient } from '../../api/client';

export interface GroundingStatusData {
  service?: string;
  is_running?: boolean;
  uptime_seconds?: number;
  total_ingested_count?: number;
  cached_candle_count?: number;
  audit_record_count?: number;
  error_count?: number;
  last_error?: string | null;
  last_cycle_utc?: string;
  telemetry_freshness?: {
    last_ingestion_utc?: string | null;
    last_derived_snapshot_utc?: string | null;
    last_price_probe_utc?: string | null;
    last_completed_cycle_utc?: string | null;
  };
  p95_latency_ms?: number;
  database?: {
    backend?: string;
    healthy?: boolean;
    pool_active?: number;
    pool_available?: number;
    replication_lag_seconds?: string;
    outbox_pending_count?: number;
    error?: string;
    [key: string]: unknown;
  };
  spot_symbols?: string[];
  perp_symbols?: string[];
  timeframes?: string[];
  active_providers?: Record<string, string>;
}

export interface GroundingTelemetryData {
  status?: string;
  cpu_percent?: number;
  load_avg_1m?: number;
  container_ram_used_bytes?: number;
  host_ram_used_bytes?: number;
  host_ram_total_bytes?: number;
  host_ram_percent?: number;
  memory_percent?: number;
  container_memory_rss_bytes?: number;
  disk_data_used_bytes?: number;
  disk_data_total_bytes?: number;
  disk_data_percent?: number;
  disk_percent?: number;
  host_load_1m?: number;
  as_of_utc?: string;
  [key: string]: unknown;
}

export interface ApiMeshDomain {
  domain_id: string;
  name: string;
  description: string;
  status: string;
  endpoints: Array<{
    method: string;
    path_template: string;
    description: string;
  }>;
}

export interface GroundingSnapshotPayload {
  schema_version: string;
  snapshot_utc: string;
  snapshot_age_seconds: number;
  service: string;
  status: 'fresh' | 'stale' | 'loading' | 'disconnected' | 'empty';
  cache_ttl_seconds: number;
  database_topology: {
    db1_crypto: {
      store_id: string;
      engine: string;
      provenance: string;
      isolation_policy: string;
      probe_attempted: boolean;
      observed: boolean;
      permitted_connections: number;
      description: string;
    };
    db2_multiasset: {
      store_id: string;
      engine: string;
      provenance: string;
      status: string;
      pool_active: number | null;
      pool_available: number | null;
      pool_max: number | null;
      latest_candle_utc: string | null;
      hypertables: string[];
    };
  };
  api_mesh: {
    provenance: string;
    total_domains: number;
    domains: ApiMeshDomain[];
  };
  schedulers_and_workers: {
    declared_windows: Array<{
      provenance: string;
      name: string;
      schedule_cst: string;
      target: string;
    }>;
    observed_runtime: {
      provenance: string;
      engine_running: boolean | null;
      uptime_seconds: number | null;
      last_cycle_utc: string | null;
      cached_candle_count: number | null;
      total_ingested_count: number | null;
    };
  };
  host_and_containers: {
    declared_containers: Array<{
      provenance: string;
      role: string;
      service_name: string;
      network_exposure: string;
    }>;
    observed_compute: {
      provenance: string;
      cpu_percent: string | null;
      ram_percent: string | null;
      disk_percent: string | null;
      p95_latency_ms: string | null;
    };
  };
  governance_and_invariants: {
    provenance: string;
    protocol_version: string;
    execution_profile: string;
    core_invariants: Array<{
      id: string;
      title: string;
      rule: string;
    }>;
  };
}

export type GroundingState = 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
export type GroundingTab = 'runtime' | 'api_mesh' | 'containers_timers' | 'governance' | 'topology' | 'invariants';

export const ProjectGroundingView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<GroundingTab>('runtime');
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [domainFilter, setDomainFilter] = useState('');

  // 5-state machine for truthful runtime observation
  const [overallState, setOverallState] = useState<GroundingState>('loading');
  const [healthData, setHealthData] = useState<{ status: string } | null>(null);
  const [statusData, setStatusData] = useState<GroundingStatusData | null>(null);
  const [telemetryData, setTelemetryData] = useState<GroundingTelemetryData | null>(null);
  const [snapshotData, setSnapshotData] = useState<GroundingSnapshotPayload | null>(null);
  const [lastFetchedUtc, setLastFetchedUtc] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastSuccessMsRef = useRef<number | null>(null);

  const fetchData = useCallback(async () => {
    setIsRefreshing(true);
    let hRes: { status: string } | null = null;
    let sRes: GroundingStatusData | null = null;
    let tRes: GroundingTelemetryData | null = null;
    let snapRes: GroundingSnapshotPayload | null = null;

    try {
      const [h, s, t, snap] = await Promise.allSettled([
        apiClient.getHealth(),
        apiClient.getStatus(),
        apiClient.getTelemetry(),
        apiClient.get<GroundingSnapshotPayload>('/api/v1/meta/grounding/snapshot'),
      ]);

      if (h.status === 'fulfilled') hRes = h.value;
      if (s.status === 'fulfilled') sRes = s.value as GroundingStatusData;
      if (t.status === 'fulfilled') tRes = t.value as GroundingTelemetryData;
      if (snap.status === 'fulfilled') snapRes = snap.value;

      setHealthData(hRes);
      setStatusData(sRes);
      setTelemetryData(tRes);
      if (snapRes) setSnapshotData(snapRes);

      const anyReceived = Boolean(hRes || sRes || tRes || snapRes);
      const allReceived = Boolean(hRes && sRes && tRes && snapRes);

      if (!anyReceived) {
        if (lastSuccessMsRef.current && Date.now() - lastSuccessMsRef.current < 30000) {
          setOverallState('stale');
        } else {
          setOverallState('disconnected');
        }
      } else if (allReceived && snapRes?.status === 'fresh') {
        lastSuccessMsRef.current = Date.now();
        setOverallState('fresh');
        setLastFetchedUtc(new Date().toISOString());
      } else {
        lastSuccessMsRef.current = Date.now();
        setOverallState('stale');
        setLastFetchedUtc(new Date().toISOString());
      }
    } catch {
      if (lastSuccessMsRef.current && Date.now() - lastSuccessMsRef.current < 30000) {
        setOverallState('stale');
      } else {
        setOverallState('disconnected');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Dynamic Markdown Prompt 2.0 Generator
  const handleCopyPrompt = () => {
    const nowUtc = new Date().toISOString();

    const svcStatus = statusData?.is_running !== undefined
      ? (statusData.is_running ? 'RUNNING' : 'STOPPED')
      : (healthData?.status ? String(healthData.status).toUpperCase() : 'unavailable');

    const uptimeStr = statusData?.uptime_seconds !== undefined
      ? `${statusData.uptime_seconds}s`
      : (snapshotData?.schedulers_and_workers?.observed_runtime?.uptime_seconds !== null && snapshotData?.schedulers_and_workers?.observed_runtime?.uptime_seconds !== undefined
          ? `${snapshotData.schedulers_and_workers.observed_runtime.uptime_seconds}s`
          : 'unavailable');

    const p95Str = statusData?.p95_latency_ms !== undefined
      ? `${statusData.p95_latency_ms}ms`
      : (snapshotData?.host_and_containers?.observed_compute?.p95_latency_ms ?? 'unavailable');

    const sysRes = (telemetryData?.system_resources as Record<string, unknown> | undefined) || (telemetryData as Record<string, unknown> | undefined);
    const cpuStr = sysRes?.cpu_percent !== undefined
      ? `${sysRes.cpu_percent}%`
      : (snapshotData?.host_and_containers?.observed_compute?.cpu_percent ?? 'unavailable');

    const ramPercentVal = sysRes?.host_ram_percent ?? sysRes?.memory_percent;
    const ramStr = ramPercentVal !== undefined
      ? `${ramPercentVal}%`
      : (snapshotData?.host_and_containers?.observed_compute?.ram_percent ?? 'unavailable');

    const diskPercentVal = sysRes?.disk_data_percent ?? sysRes?.disk_percent;
    const diskStr = diskPercentVal !== undefined
      ? `${diskPercentVal}%`
      : (snapshotData?.host_and_containers?.observed_compute?.disk_percent ?? 'unavailable');

    const dbHealthyStr = statusData?.database?.healthy !== undefined
      ? (statusData.database.healthy ? 'HEALTHY' : 'UNHEALTHY')
      : (snapshotData?.database_topology?.db2_multiasset?.status ?? 'unavailable');

    const cachedCandlesStr = statusData?.cached_candle_count !== undefined
      ? String(statusData.cached_candle_count)
      : (snapshotData?.schedulers_and_workers?.observed_runtime?.cached_candle_count !== null && snapshotData?.schedulers_and_workers?.observed_runtime?.cached_candle_count !== undefined
          ? String(snapshotData.schedulers_and_workers.observed_runtime.cached_candle_count)
          : 'unavailable');

    const totalIngestedStr = statusData?.total_ingested_count !== undefined
      ? String(statusData.total_ingested_count)
      : (snapshotData?.schedulers_and_workers?.observed_runtime?.total_ingested_count !== null && snapshotData?.schedulers_and_workers?.observed_runtime?.total_ingested_count !== undefined
          ? String(snapshotData.schedulers_and_workers.observed_runtime.total_ingested_count)
          : 'unavailable');

    const lastCycleStr = statusData?.last_cycle_utc || snapshotData?.schedulers_and_workers?.observed_runtime?.last_cycle_utc || 'unavailable';

    const db1Desc = snapshotData?.database_topology?.db1_crypto
      ? `store_id: CRYPTO_PRIMARY_ISOLATION | engine: TimescaleDB | policy: STRICT_ISOLATION_LOCKED | probe_attempted: false | connections: 0`
      : 'store_id: CRYPTO_PRIMARY_ISOLATION | policy: STRICT_ISOLATION_LOCKED (Air-gapped)';

    const db2Desc = snapshotData?.database_topology?.db2_multiasset
      ? `store_id: MULTIASSET_ACTIVE | status: ${snapshotData.database_topology.db2_multiasset.status} | pool_active: ${snapshotData.database_topology.db2_multiasset.pool_active ?? 'N/A'} | pool_avail: ${snapshotData.database_topology.db2_multiasset.pool_available ?? 'N/A'} | hypertables: ${snapshotData.database_topology.db2_multiasset.hypertables.join(', ')}`
      : `store_id: MULTIASSET_ACTIVE | status: ${dbHealthyStr}`;

    const apiMeshRows = (snapshotData?.api_mesh?.domains || []).map(d =>
      `- [${d.domain_id}] ${d.name} (${d.status}): ${d.endpoints.map(e => `${e.method} ${e.path_template}`).join(', ')}`
    ).join('\n');

    const protocolVer = snapshotData?.governance_and_invariants?.protocol_version || 'ASOP v1.9.6';
    const protocolPin = `docs/governance/ASOP/ASOP.md@${protocolVer.replace('ASOP v', '')}`;

    const prompt = `# Market Hub Agent Grounding Context (${protocolVer})
## 1. Protocol & Profile
- Protocol Pin: ${protocolPin}
- Execution Profile: EXEC-MARKET_HUB-MULTI-COORDINATED
- Collaboration Mode: Tri-Role Multi-Agent (PM Authority ↔ Codex Agent-A Control ↔ Gemini Agent-B Execution)
- Grounding State: ${overallState.toUpperCase()} (Snapshot: ${nowUtc})
- Holographic Cache TTL: 600s Monotonic Mutex Lock

## 2. Dual Database Architecture & SSOT Topology
- Store 1 (Crypto Primary): ${db1Desc}
- Store 2 (MultiAsset Active): ${db2Desc}
- Decoupling Invariant: 100% physically decoupled. Zero network probes to primary crypto store.

## 3. 11-Domain Quantitative API Mesh Catalog
${apiMeshRows || '- 11 Quant Domains Active: ashare_screener, ashare_watchlist, ashare_index, ashare_ct, ashare_tags, ashare_sector, ashare_main_profile, crypto_market_data, crypto_indicators, crypto_regime, ecosystem_meta'}

## 4. Background Workers & Systemd Schedules
- Midday A-Share Ingress: 11:45 CST (market-hub-midday-ingress.timer)
- EOD A-Share Ingress: 15:15 CST (market-hub-eod-ingress.timer)
- Resampling Horizon: Causal closed-bar W >= 3s

## 5. Observed Live Runtime Facts
- Service Status: ${svcStatus}
- Core Engine Uptime: ${uptimeStr}
- Ingestion P95 Latency: ${p95Str}
- System Compute: CPU: ${cpuStr} | RAM: ${ramStr} | Disk: ${diskStr}
- Database SSOT: Crypto TimescaleDB (STRICT_ISOLATION_LOCKED) | MultiAsset TimescaleDB (${dbHealthyStr})
- Telemetry Watermarks: Cached Candles: ${cachedCandlesStr} | Ingested Total: ${totalIngestedStr}
- Last Worker Cycle UTC: ${lastCycleStr}
- Active Diagnostic Endpoints: /health, /api/v1/market/status, /api/v1/market/telemetry, /api/v1/meta/grounding/snapshot

## 6. Non-Negotiable Core Invariants (Fail-Closed)
1. GATE-NEG-01 (Zero Float): Financial prices, turnover, indicators strictly use Decimal or canonical string format. Binary float arithmetic is forbidden.
2. GATE-NEG-02 (Causal Bar Closure): Only validated, causally closed bars (t) with lag window >= 3s are delivered to indicators.
3. GATE-NEG-03 (Closed-Market Frozen State): Weekend/holiday virtual candle synthesis is prohibited.
4. GATE-NEG-04 (Replay Defense): Tenant gateway enforces UTC ISO timestamp window (+/-5.0s) and UUID4 nonce store.
5. GATE-NEG-05 (Read-Only Lock): Ingress and endpoints strictly reject mutating HTTP methods (POST, PUT, PATCH, DELETE) with HTTP 405 Method Not Allowed.
`;
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  const getBadgeClass = (state: GroundingState) => {
    switch (state) {
      case 'fresh': return 'badge badge-bullish';
      case 'stale': return 'badge badge-warning';
      case 'loading': return 'badge badge-cyan';
      case 'disconnected': return 'badge badge-bearish';
      default: return 'badge';
    }
  };

  const filteredDomains = (snapshotData?.api_mesh?.domains || []).filter(d =>
    !domainFilter ||
    d.name.toLowerCase().includes(domainFilter.toLowerCase()) ||
    d.domain_id.toLowerCase().includes(domainFilter.toLowerCase()) ||
    d.endpoints.some(e => e.path_template.toLowerCase().includes(domainFilter.toLowerCase()))
  );

  return (
    <div className="view-panel active" id="view-grounding-hub" role="tabpanel">
      {/* Header Row */}
      <div className="view-header" style={{ paddingBottom: '4px', marginBottom: '4px' }}>
        <div className="view-title-group bilingual-view-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🧭</span>
            <h2 className="view-main-title bilingual-header-title-zh" style={{ margin: 0, fontSize: '16px' }}>
              项目全息接地中枢
            </h2>
          </div>
          <span className="bilingual-header-subtitle-en">PROJECT HOLOGRAPHIC GROUNDING HUB</span>
          <p className="view-subtitle" style={{ margin: '1px 0 0 0' }}>
            Quant 全域状态感知 · 双时序 SSOT · 11 大量化网格 · 容器调度 · 不变量中枢 (ASOP v1.9.6)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            className="btn-refresh"
            onClick={fetchData}
            title="刷新运行时状态"
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', cursor: 'pointer', fontSize: '11px' }}
          >
            <RefreshCw size={11} className={isRefreshing ? 'spin' : ''} />
            <span>刷新</span>
          </button>
          <button
            className="btn-copy-grounding"
            id="btn-copy-grounding-prompt"
            onClick={handleCopyPrompt}
            title="生成包含实时全息运行态快照的标准 Agent 接地 Prompt 2.0"
            style={{ padding: '4px 10px', fontSize: '11px' }}
          >
            {copiedPrompt ? <Check size={12} /> : <Copy size={12} />}
            <span>{copiedPrompt ? '已复制 Grounding Prompt 2.0' : '一键复制 Agent 接地 Prompt 2.0'}</span>
          </button>
        </div>
      </div>

      {/* 4 KPI Summary Cards */}
      <div className="grounding-kpi-grid">
        <div className="grounding-kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">ASOP 协议版本</span>
            <span className="badge badge-bullish">ACTIVE</span>
          </div>
          <div className="kpi-metric">{snapshotData?.governance_and_invariants?.protocol_version || 'ASOP v1.9.6'}</div>
          <div className="kpi-sub">Profile: EXEC-MARKET_HUB-MULTI-COORDINATED</div>
        </div>

        <div className="grounding-kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">全域 API 量化网格</span>
            <span className="badge badge-cyan">{snapshotData?.api_mesh?.total_domains ?? 11} DOMAINS</span>
          </div>
          <div className="kpi-metric" style={{ color: 'var(--accent-cyan)' }}>11 域全量编排</div>
          <div className="kpi-sub">A股因子 + 加密指标 + 宏观共振</div>
        </div>

        <div className="grounding-kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">双时序数据库 SSOT</span>
            <span className={snapshotData?.database_topology?.db2_multiasset?.status === 'HEALTHY' ? 'badge badge-bullish' : 'badge badge-warning'}>
              {snapshotData?.database_topology?.db2_multiasset?.status ?? 'ONLINE'}
            </span>
          </div>
          <div className="kpi-metric" style={{ color: 'var(--color-emerald)' }}>物理解耦隔离</div>
          <div className="kpi-sub">CRYPTO_PRIMARY ↔ MULTIASSET</div>
        </div>

        <div className="grounding-kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">运行时真理态</span>
            <span className={getBadgeClass(overallState)}>{overallState.toUpperCase()}</span>
          </div>
          <div className="kpi-metric" style={{ color: overallState === 'fresh' ? 'var(--color-emerald)' : 'var(--color-rose)' }}>
            {overallState === 'fresh' ? '5-STATE VALID' : overallState.toUpperCase()}
          </div>
          <div className="kpi-sub">
            {lastFetchedUtc ? `快照龄: ${snapshotData?.snapshot_age_seconds ?? 0}s (TTL: 600s)` : '等待采样...'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="grounding-tabs-container">
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'runtime' ? 'active' : ''}`}
          onClick={() => setActiveTab('runtime')}
        >
          <div className="bilingual-tab-title-zh">
            <Database size={13} />
            <span>1. 双时序数据库 SSOT 拓扑</span>
          </div>
          <span className="bilingual-tab-subtitle-en">TIMESCALE SSOT TOPOLOGY</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'api_mesh' ? 'active' : ''}`}
          onClick={() => setActiveTab('api_mesh')}
        >
          <div className="bilingual-tab-title-zh">
            <Network size={13} />
            <span>2. 11 大量化域全域 API 网格</span>
          </div>
          <span className="bilingual-tab-subtitle-en">11-DOMAIN API MESH</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'containers_timers' ? 'active' : ''}`}
          onClick={() => setActiveTab('containers_timers')}
        >
          <div className="bilingual-tab-title-zh">
            <Layers size={13} />
            <span>3. 容器角色与定时调度流</span>
          </div>
          <span className="bilingual-tab-subtitle-en">CONTAINERS & TIMERS</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'governance' ? 'active' : ''}`}
          onClick={() => setActiveTab('governance')}
        >
          <div className="bilingual-tab-title-zh">
            <ShieldCheck size={13} />
            <span>4. 协议与治理准则</span>
          </div>
          <span className="bilingual-tab-subtitle-en">GOVERNANCE & ASOP</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'topology' ? 'active' : ''}`}
          onClick={() => setActiveTab('topology')}
        >
          <div className="bilingual-tab-title-zh">
            <Server size={13} />
            <span>5. 生态逻辑拓扑</span>
          </div>
          <span className="bilingual-tab-subtitle-en">ECOSYSTEM TOPOLOGY</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'invariants' ? 'active' : ''}`}
          onClick={() => setActiveTab('invariants')}
        >
          <div className="bilingual-tab-title-zh">
            <Lock size={13} />
            <span>6. 不变量与安全网关</span>
          </div>
          <span className="bilingual-tab-subtitle-en">INVARIANTS & GATEWAYS</span>
        </button>
      </div>

      {/* Tab Panel 1: 双时序数据库 SSOT 拓扑 */}
      {activeTab === 'runtime' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Section: 存储底座与时序数据水位 */}
          <div className="glass-panel" style={{ padding: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Database size={16} color="var(--color-emerald)" />
                <div className="bilingual-card-title">
                  <h4 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                    双时序数据库底座与水位
                  </h4>
                  <span className="bilingual-card-subtitle-en">TIMESCALE SSOT & WATERMARKS</span>
                </div>
              </div>
              <span className="badge badge-bullish">HYPERTABLE ACTIVE</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', fontSize: '12px' }}>
              {/* Crypto DB */}
              <div style={{ background: 'var(--bg-surface-hover)', padding: '14px', borderRadius: '6px', border: '1px solid var(--border-subtle)', borderLeft: '4px solid var(--color-rose)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 700, color: 'var(--color-rose)' }}>CRYPTO_PRIMARY_ISOLATION</span>
                  <span className="badge badge-bearish" style={{ fontSize: '10px' }}>ISOLATED</span>
                </div>
                <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  <div>• 引擎：TimescaleDB</div>
                  <div>• 治理策略：<strong>STRICT_ISOLATION_LOCKED</strong></div>
                  <div>• 零网络探测：probe_attempted = false</div>
                  <div>• 许可外部连接数：0 (纯静态元数据守卫)</div>
                  <div>• 访问权限：纯只读保护，严禁变异</div>
                </div>
              </div>

              {/* MultiAsset DB */}
              <div style={{ background: 'var(--bg-surface-hover)', padding: '14px', borderRadius: '6px', border: '1px solid var(--border-subtle)', borderLeft: '4px solid var(--color-emerald)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 700, color: 'var(--color-emerald)' }}>MULTIASSET_ACTIVE</span>
                  <span className={snapshotData?.database_topology?.db2_multiasset?.status === 'HEALTHY' || statusData?.database?.healthy ? 'badge badge-bullish' : 'badge badge-warning'} style={{ fontSize: '10px' }}>
                    {snapshotData?.database_topology?.db2_multiasset?.status ?? (statusData?.database?.healthy ? 'HEALTHY' : (statusData?.database?.healthy === false ? 'UNHEALTHY' : 'unavailable'))}
                  </span>
                </div>
                <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  <div>• 引擎：TimescaleDB (活跃运行时)</div>
                  <div>• 连接池状态：活跃 {snapshotData?.database_topology?.db2_multiasset?.pool_active ?? 'N/A'} / 可用 {snapshotData?.database_topology?.db2_multiasset?.pool_available ?? 'N/A'} / 上限 {snapshotData?.database_topology?.db2_multiasset?.pool_max ?? 'N/A'}</div>
                  <div>• 最新入库时间戳：{snapshotData?.database_topology?.db2_multiasset?.latest_candle_utc ?? 'unavailable'}</div>
                  <div>• 活跃 Hypertables：{snapshotData?.database_topology?.db2_multiasset?.hypertables?.join(', ') ?? 'ashare_kline_1m, ashare_screener_daily'}</div>
                </div>
              </div>

              {/* Data Ingress & Active Feeds */}
              <div style={{ background: 'var(--bg-surface-hover)', padding: '14px', borderRadius: '6px', border: '1px solid var(--border-subtle)', borderLeft: '4px solid var(--accent-cyan)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 700, color: 'var(--accent-cyan)' }}>时序摄取与活跃标的池</span>
                  <span className="badge badge-cyan" style={{ fontSize: '10px' }}>ACTIVE FEEDS</span>
                </div>
                <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  <div>• 活跃摄取标的：<strong>{statusData?.spot_symbols?.length ?? 7} 核心标的 ({statusData?.spot_symbols?.slice(0, 4).join(', ') ?? 'BTC, ETH, SOL, BNB'} 等)</strong></div>
                  <div>• 连续时序周期：<strong>{statusData?.timeframes?.length ?? 7} 周期 (1m, 5m, 15m, 30m, 1h, 4h, 1d)</strong></div>
                  <div>• 内存计算缓冲：<strong>最新 {statusData?.cached_candle_count ?? 300} Bar (因果时延 W≥3s)</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Panel 2: 11 大量化域全域 API 网格 */}
      {activeTab === 'api_mesh' && (
        <div className="glass-panel" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <div className="bilingual-card-title">
                <h4 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: 'var(--accent-cyan)' }} className="bilingual-card-title-zh">
                  11 大量化能力域公开交互网格
                </h4>
                <span className="bilingual-card-subtitle-en">QUANT CAPABILITY MESH CATALOG</span>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                所有端点仅暴露 GET/HEAD 纯只读方法，杜绝内部端口与敏感路由泄露
              </p>
            </div>
            <input
              type="text"
              placeholder="过滤领域或路由..."
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '12px' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
            {filteredDomains.map((domain) => (
              <div key={domain.domain_id} style={{ background: 'var(--bg-surface-hover)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '13px' }}>{domain.name}</span>
                  <span className="badge badge-bullish" style={{ fontSize: '10px' }}>{domain.status}</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  {domain.description} (<code>{domain.domain_id}</code>)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {domain.endpoints.map((ep, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', background: 'var(--bg-surface)', padding: '4px 8px', borderRadius: '4px' }}>
                      <span className="mono" style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>{ep.method}</span>
                      <span className="mono" style={{ color: 'var(--text-secondary)' }}>{ep.path_template}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Panel 3: 容器角色与定时调度流 */}
      {activeTab === 'containers_timers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Containers */}
          <div className="glass-panel" style={{ padding: '18px' }}>
            <div className="bilingual-card-title" style={{ marginBottom: '12px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: 'var(--accent-cyan)' }} className="bilingual-card-title-zh">
                服务容器运行角色
              </h4>
              <span className="bilingual-card-subtitle-en">DECLARED CONTAINER ROLES</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', fontSize: '12px' }}>
              {(snapshotData?.host_and_containers?.declared_containers || [
                { role: 'Core Quantitative API & Ingress Daemon', service_name: 'tso-market-hub', network_exposure: 'Internal Container Network' },
                { role: 'Caddy Reverse Proxy & TLS Gateway', service_name: 'tso-market-hub-proxy', network_exposure: 'Public Ingress (80/443)' },
                { role: 'MultiAsset TimescaleDB Engine', service_name: 'tso-market-hub-timescaledb-multiasset', network_exposure: 'Internal Private Isolation' },
                { role: 'Isolated Crypto TimescaleDB Store', service_name: 'tso-market-hub-timescaledb', network_exposure: 'Air-Gapped Private Storage' },
                { role: 'Ingress BFF Gateway Guard', service_name: 'tso-market-hub-ingress-bff', network_exposure: 'Internal BFF Defense' },
              ]).map((c, i) => (
                <div key={i} style={{ background: 'var(--bg-surface-hover)', padding: '12px', borderRadius: '6px', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{c.service_name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '6px' }}>{c.role}</div>
                  <span className="badge badge-cyan" style={{ fontSize: '10px' }}>{c.network_exposure}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Schedulers */}
          <div className="glass-panel" style={{ padding: '18px' }}>
            <div className="bilingual-card-title" style={{ marginBottom: '12px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: 'var(--color-warning)' }} className="bilingual-card-title-zh">
                Linux 定时调度任务
              </h4>
              <span className="bilingual-card-subtitle-en">SYSTEMD SCHEDULES</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', fontSize: '12px' }}>
              {(snapshotData?.schedulers_and_workers?.declared_windows || [
                { name: 'Midday A-Share Ingress', schedule_cst: '11:45 CST', target: 'market-hub-midday-ingress.timer' },
                { name: 'EOD A-Share Batch Ingress', schedule_cst: '15:15 CST', target: 'market-hub-eod-ingress.timer' },
              ]).map((w, i) => (
                <div key={i} style={{ background: 'var(--bg-surface-hover)', padding: '14px', borderRadius: '6px', border: '1px solid var(--border-subtle)', borderLeft: '4px solid var(--color-warning)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{w.name}</span>
                    <span className="badge badge-warning">{w.schedule_cst}</span>
                  </div>
                  <div className="mono" style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{w.target}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab Panel 4: 协议与治理准则 */}
      {activeTab === 'governance' && (
        <div className="glass-panel" style={{ padding: '20px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px', color: 'var(--accent-cyan)' }}>
            ASOP 人机协同权责制衡模型 (Tri-Role Authority Model · {snapshotData?.governance_and_invariants?.protocol_version?.replace('ASOP ', '') || 'v1.9.6'})
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', fontSize: '12px' }}>
            <div style={{ background: 'var(--bg-surface-hover)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-subtle)', borderLeft: '4px solid var(--color-warning)' }}>
              <div style={{ fontWeight: 700, color: 'var(--color-warning)', fontSize: '13px', marginBottom: '6px' }}>PM (Human Owner)</div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>业务决策最高权力者。负责业务需求提出、边界扩充裁决、重大风险签署与最终发布批准。</div>
            </div>
            <div style={{ background: 'var(--bg-surface-hover)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-subtle)', borderLeft: '4px solid var(--accent-cyan)' }}>
              <div style={{ fontWeight: 700, color: 'var(--accent-cyan)', fontSize: '13px', marginBottom: '6px' }}>Agent-A (Codex)</div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>系统架构与质检守门人。负责前置任务核验 (00_TASK)、边界约束把关与交付终审验收 (30_REVIEW)。</div>
            </div>
            <div style={{ background: 'var(--bg-surface-hover)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-subtle)', borderLeft: '4px solid var(--color-emerald)' }}>
              <div style={{ fontWeight: 700, color: 'var(--color-emerald)', fontSize: '13px', marginBottom: '6px' }}>Agent-B (Gemini / Antigravity)</div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>敏捷执行与 PM 首席技术参谋。负责授权边界内高速代码交付、正反向测试、真实证据闭环与决策建议。</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Panel 5: 生态逻辑拓扑 */}
      {activeTab === 'topology' && (
        <div className="glass-panel" style={{ padding: '20px' }}>
          <div className="bilingual-card-title" style={{ marginBottom: '14px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: 'var(--accent-cyan)' }} className="bilingual-card-title-zh">
              Quant 生态数据接入与分发拓扑
            </h4>
            <span className="bilingual-card-subtitle-en">DATA INGRESS & DISTRIBUTION TOPOLOGY</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <div>• <strong>交易所采集层</strong>：Binance, OKX, Bybit, Bitget, Hyperliquid 多通道 WebSocket/REST 协同</div>
            <div>• <strong>核心服务层</strong>：FastAPI 异步非阻塞处理引擎，因果重采样，指标共振计算</div>
            <div>• <strong>网关防御层</strong>：Ingress BFF 动态注入内部 HMAC 签名与租户防重放校验</div>
            <div>• <strong>下游生态消费方</strong>：Trading-Strategy-Orchestrator (TSO 交易引擎), Doctor-A (量化风险分析)</div>
          </div>
        </div>
      )}

      {/* Tab Panel 6: 不变量与安全网关 (完整门禁态势矩阵) */}
      {activeTab === 'invariants' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="glass-panel" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldCheck size={16} color="var(--accent-cyan)" />
                <div className="bilingual-card-title">
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }} className="bilingual-card-title-zh">
                    核心安全防御门禁态势
                  </h3>
                  <span className="bilingual-card-subtitle-en">FAIL-CLOSED CORE INVARIANTS STATUS</span>
                </div>
              </div>
              <span className="badge badge-bullish">5 / 5 ACTIVE</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: 'var(--bg-surface-hover)', borderBottom: '1px solid var(--border-subtle)', textAlign: 'left' }}>
                  <th style={{ padding: '10px 14px', color: 'var(--text-muted)', fontWeight: 600 }}>门禁代号</th>
                  <th style={{ padding: '10px 14px', color: 'var(--text-muted)', fontWeight: 600 }}>约束定义</th>
                  <th style={{ padding: '10px 14px', color: 'var(--text-muted)', fontWeight: 600 }}>工程校验机制</th>
                  <th style={{ padding: '10px 14px', color: 'var(--text-muted)', fontWeight: 600, textAlign: 'center' }}>现行状态</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td className="mono" style={{ padding: '10px 14px', color: 'var(--accent-cyan)', fontWeight: 700 }}>GATE-NEG-01</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>金融零浮点约束</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>AST 语法树校验 + Decimal 规范化，严禁原生 float 运算</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}><span className="badge badge-bullish">ENFORCED</span></td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td className="mono" style={{ padding: '10px 14px', color: 'var(--accent-cyan)', fontWeight: 700 }}>GATE-NEG-02</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>因果闭合烛线约束</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>因果重采样器判定，未收盘 Bar 拒绝触发因子共振更新</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}><span className="badge badge-bullish">ENFORCED</span></td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td className="mono" style={{ padding: '10px 14px', color: 'var(--accent-cyan)', fontWeight: 700 }}>GATE-NEG-03</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>非连续市场闭市冻结</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>A股/美股非交易时段拒绝合成虚拟数据，状态定格于最终收盘 Bar</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}><span className="badge badge-bullish">ENFORCED</span></td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td className="mono" style={{ padding: '10px 14px', color: 'var(--accent-cyan)', fontWeight: 700 }}>GATE-NEG-04</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>网关租户防重放防御</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>UTC 客户端时间戳窗口 (±5.0s) 与 UUID4 Nonce 幂等单次消费</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}><span className="badge badge-bullish">ENFORCED</span></td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td className="mono" style={{ padding: '10px 14px', color: 'var(--accent-cyan)', fontWeight: 700 }}>GATE-NEG-05</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>只读网关熔断锁</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>变异 HTTP 方法 (POST/PUT/DELETE) 统一拦截并返回 HTTP 405</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}><span className="badge badge-bullish">ENFORCED</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sleek Compact Footer Guardrail Strip (High Density & Zero Clutter) */}
      <div
        className="glass-panel"
        style={{
          padding: '10px 16px',
          marginTop: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '12px',
          borderRadius: '8px',
          border: '1px solid var(--border-subtle)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent-cyan)', fontWeight: 600 }}>
            <ShieldCheck size={14} />
            <span>核心门禁守护:</span>
          </div>
          <span className="mono" style={{ color: 'var(--text-secondary)' }}>
            GATE-NEG-01 (纯Decimal) · GATE-NEG-02 (因果闭合 W≥3s) · GATE-NEG-03 (闭市冻结) · GATE-NEG-04 (防重放) · GATE-NEG-05 (全站只读)
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span className="badge badge-bullish" style={{ fontSize: '10px' }}>5 / 5 ACTIVE</span>
          {activeTab !== 'invariants' && (
            <button
              onClick={() => setActiveTab('invariants')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-cyan)',
                cursor: 'pointer',
                fontSize: '11px',
                padding: 0,
                textDecoration: 'underline'
              }}
            >
              查看门禁矩阵 →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
